import {
  getCollectionsFieldMap,
  PAGE_TYPES,
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
} from '@wix/wix-data-client-common'
import { getProgressBar } from '../../progress-bar/executeSteps'
import { getRouterPrefixAndPattern } from '../add-dynamic-item-page/getRouterPrefixAndPattern'
import { updateDatasetAndBindings } from '../add-dynamic-item-page/updateDatasetAndBindings'
import { findController } from '../utils/findController'
import { addRouter } from '../add-collection-and-dynamic-item-page/addRouter'
import { addPage } from '../add-collection-and-dynamic-item-page/addPage'

import type {
  AppApi,
  Editor3DynamicPagesBiParams,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { PresetDefinition } from '../../presets/types'
import { type CollectionsApi } from '@wix/wix-code-collections-api'
import { addMainSlugFieldIfNotExists } from '../../slug/addSlugField'
import { generateUniquePageLinkId } from '../../slug/generatePageLinkId'
import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'
import type { PageAddResult } from '../add-cms-pages/addCmsPages'

type Params = {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  existingCollectionId: string
  i18n: I18n
  fedopsLogger: FedopsLogger
  logger: Logger
  biParams: Editor3DynamicPagesBiParams
}

export const addItemPage = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  existingCollectionId,
  pageLinkId,
}: Omit<Params, 'fedopsLogger' | 'i18n' | 'logger' | 'biParams'> & {
  pageLinkId: string
}): Promise<PageAddResult> => {
  const newCollectionSchema = await collectionsApi.get(existingCollectionId)

  const { newRouterPrefix, pattern } = await getRouterPrefixAndPattern({
    editorSdkProxy,
    appApi,
    newCollectionSchema,
  })

  const { pageRef } = await addPage({
    editorSdkProxy,
    preset,
    newCollectionName: newCollectionSchema.displayName,
  })

  const { controllerRef } = await findController({
    editorSdkProxy,
    pageRef,
  })

  await addRouter({
    editorSdkProxy,
    newCollectionSchema,
    presetRouter: preset.routers[0],
    pageRef,
    pattern,
    newRouterPrefix,
    pageLinkId,
  })

  const fieldMap = await getCollectionsFieldMap({
    appApi,
    controllerRef,
    sourceSchema: preset.collections[0].schema,
    destinationSchema: newCollectionSchema,
  })

  await updateDatasetAndBindings({
    appApi,
    controllerRef,
    newCollectionId: newCollectionSchema.id,
    newCollectionDisplayName: newCollectionSchema.displayName,
    fieldMap,
  })

  return { pageRef, schema: newCollectionSchema, newRouterPrefix, pattern }
}

const interactionName = 'addDynamicItemPage'

export const addDynamicItemPage = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  existingCollectionId,
  i18n,
  fedopsLogger,
  logger,
  biParams,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 1,
    title: i18n.t('Editor3_Add_Pages_Progress_Bar_Title_Creating_Item_Pages'),
  })
  await progressBar.open()

  let result: PageAddResult

  try {
    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Adding_Items_Page',
      ),
    })

    await addMainSlugFieldIfNotExists({
      collectionsApi,
      collectionId: existingCollectionId,
    })

    const pageLinkId = await generateUniquePageLinkId({ editorSdkProxy })

    result = await runTransactionWithRetry(editorSdkProxy, async () => {
      return addItemPage({
        editorSdkProxy,
        appApi,
        preset,
        collectionsApi,
        existingCollectionId,
        pageLinkId,
      })
    })

    await progressBar.update()
  } finally {
    await progressBar.close()
  }

  const { newRouterPrefix, pattern, schema, pageRef } = result
  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: PAGE_TYPES.ITEM,
      collection_name: schema.displayName,
      collection_id: existingCollectionId,
      new_url: `${newRouterPrefix}${pattern}`,
      is_auto_bound: true,
      new_collection: false,
      pageId: pageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )

  await appApi.handleChangedRouters({ pageRefs: [pageRef] })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_ROUTERS,
    params: { pageRefs: [pageRef] },
  })

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef })

  return { pageRef }
}
